import React from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

// components / controls
import JxTitleBar from '../../../JxControls/JxTitleBar/JxTitleBar';

// utils
import bitvavoPriceHelper from '../../../utils/BitvavoPriceHelper';

// core
import cardDataEngine from '../Core/CardDataEngine';
import PainterBase from '../Core/PainterBase';
import TriggerKind from '../Types/TriggerKind';

// local
import CryptoTickerCache from './CryptoTickerCache';
import CryptoTickerTriggerDispatcher from './CryptoTickerTriggerDispatcher';
import './CryptoTickerWidget.css';

// CryptoTickerWidget/CryptoTickerWidget.jsx
// Extends PainterBase — draggable ticker card, owns its own Cache + TriggerDispatcher.
class CryptoTickerWidget extends PainterBase {
    constructor(props) {
        super(props);
        this.name = `CryptoTickerWidget_${props.meta.target}`; // required by JxForm — unique per (type, target) instance
        this.schema = null; // no Joi validation — no form fields

        // Merge onto base-class state — don't replace outright, panelStyle reads this.state.geo.
        this.state = { ...this.state, data: null };

        this._cache = new CryptoTickerCache();
        this._cache.initialize();

        this._dispatcher = new CryptoTickerTriggerDispatcher({ triggerKind: TriggerKind.ON_DATA_RECEIVED }, (state) => this.onPaint(state));
        this._dispatcher.initialize();

        this._cache.onDataBufferUpdated((state) => this._dispatcher.dispatch(state));
    }

    componentDidMount() {
        super.componentDidMount();
        cardDataEngine.subscribe(this.props.meta, this);
    }

    componentWillUnmount() {
        cardDataEngine.unsubscribe(this.props.meta, this);
        super.componentWillUnmount();
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps); // required — otherwise no bringToTop/focus/toolbar color update
    }

    // width=150 matches CSS; height=100 estimated (CSS has no fixed height)
    getDefaultGeo() {
        return { top: 120, left: 120, width: 150, height: 100 };
    }

    // CardDataListener subscriber contract — only this widget unpacks the envelope's shape.
    onData(envelope) {
        const ticker = JSON.parse(envelope.data); // SimpleExchangeTicker shape
        this._cache.ingest(ticker);
    }

    // PainterBase contract — called by the TriggerDispatcher when a repaint should happen.
    onPaint(state) {
        state.price = (state.ask + state.bid) / 2;

        state.price = bitvavoPriceHelper.priceRounder(state.price, false);
        state.bid = bitvavoPriceHelper.priceRounder(state.bid, false);
        state.ask = bitvavoPriceHelper.priceRounder(state.ask, false);

        this.setState({ data: state });
    }

    /**
     * @returns {object[]} JxTitleBar actions — pin toggle + close.
     */
    getTitleBarActions() {
        return [
            {
                icon: this.state.isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />,
                title: this.state.isPinned ? 'Unpin' : 'Pin on top',
                onClick: this.togglePin,
            },
            {
                icon: <CloseIcon sx={{ fontSize: 16 }} />,
                title: 'Close',
                onClick: () => this.handleClose(-1),
            },
        ];
    }

    render() {
        if (!this.props.open) return null;

        const { data } = this.state;
        const title = data?.eMarket ?? this.props.meta.target;
        const mid = data?.price ?? '-';

        const panel = (
            <div
                {...this.panelProps}
                style={this.panelStyle}
                className={`crypto-ticker-widget ${this.panelActiveClass} ${this.panelPinnedClass}`}
            >
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title={title}
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={this.getTitleBarActions()}
                />

                <div className="ctw-body">
                    {data ? (
                        <>
                            <div className="ctw-price">{mid}</div>
                            <div className="ctw-quotes">
                                <div className="ctw-quote">
                                    <span className="ctw-quote-label">bid</span>
                                    <span className="ctw-quote-value">{data.bid}</span>
                                </div>
                                <div className="ctw-quote">
                                    <span className="ctw-quote-label">ask</span>
                                    <span className="ctw-quote-value">{data.ask}</span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="ctw-loading">wachten op data…</div>
                    )}
                </div>
            </div>
        );

        return createPortal(panel, document.body);
    }
}

export default CryptoTickerWidget;
